import type { PaymentStatus, SubscriptionStatus } from '@persona/shared';
import { cn } from '../lib/cn';

type Tone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

const TONES: Record<Tone, string> = {
  success: 'bg-success-100 text-success-600',
  warning: 'bg-warning-100 text-warning-500',
  danger: 'bg-danger-100 text-danger-500',
  info: 'bg-brand-50 text-brand-600',
  neutral: 'bg-[var(--surface-muted)] text-[var(--text-secondary)]',
};

/**
 * Человеческие названия статусов.
 *
 * Пользователю важно не название состояния в базе, а что происходит с его
 * доступом и деньгами, поэтому подписи именно такие.
 */
const SUBSCRIPTION_LABELS: Record<SubscriptionStatus, { label: string; tone: Tone }> = {
  pending: { label: 'Ожидает оплаты', tone: 'warning' },
  active: { label: 'Активна', tone: 'success' },
  past_due: { label: 'Оплата не прошла', tone: 'danger' },
  canceled: { label: 'Отменена', tone: 'neutral' },
  expired: { label: 'Завершена', tone: 'neutral' },
};

const PAYMENT_LABELS: Record<PaymentStatus, { label: string; tone: Tone }> = {
  pending: { label: 'В обработке', tone: 'warning' },
  waiting_for_capture: { label: 'В обработке', tone: 'warning' },
  succeeded: { label: 'Оплачено', tone: 'success' },
  canceled: { label: 'Отменён', tone: 'neutral' },
  failed: { label: 'Не прошёл', tone: 'danger' },
  refunded: { label: 'Возвращён', tone: 'info' },
};

export function StatusPill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: Tone }) {
  return (
    <span
      className={cn('inline-flex items-center rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap', TONES[tone])}
    >
      {children}
    </span>
  );
}

export function SubscriptionStatusPill({ status }: { status: SubscriptionStatus }) {
  const { label, tone } = SUBSCRIPTION_LABELS[status];
  return <StatusPill tone={tone}>{label}</StatusPill>;
}

export function PaymentStatusPill({ status }: { status: PaymentStatus }) {
  const { label, tone } = PAYMENT_LABELS[status];
  return <StatusPill tone={tone}>{label}</StatusPill>;
}
